import Grid from "../../../Grid";
import { FORMS, MODULES } from "../../../../const";
import {
  CurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../../renderer/StyleRenderer";
import Input from "../../../input/Input";
import Button from "../../../input/Button";
import React, { useCallback, useEffect, useState } from "react";
import Dropdown from "../../../input/Dropdown";
import {
  getBulkDropdownData,
  getOtherRefDetails,
} from "../../../../api/keyvalue";
import { FiChevronsRight } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { taxCalculate } from "../../../../api/finance/chargeConfirmation";
import { saveSingleInvEv } from "../../../../store/finance/inv-ev/inv-ev-actions";
import LoadingSpinner from "../../../LoadingSpinner";
import { invEvActions } from "../../../../store/finance/inv-ev/inv-ev-slice";
import useUserCompany from "../../../../hooks/useUserCompany";
import ChargeTaxBreakdown from "../ChargeTaxBreakdown";
import SearchDropdown from "../../../input/SearchDropdown";
import { toast } from "react-toastify";

const HEADER_FORM = FORMS.EV_INV_MANAGER.HEADER.form;

const SingleTaxBreakdown = ({ gridConfig, formatter }) => {
  const userCompany = useUserCompany();
  const companyId = userCompany?.comId ?? -1;
  const auth = useSelector((state) => state.user.permissions[MODULES.Finance]);
  const evInvHeaderPermissions = auth[HEADER_FORM];

  const user = useSelector((state) => state.user);
  const singleGenEvPermission = evInvHeaderPermissions?.FORM?.SINGLE_GEN_EV;
  const singleGenInvPermission = evInvHeaderPermissions?.FORM?.SINGLE_GEN_INV;
  const changeTransporter = evInvHeaderPermissions?.FORM?.CHANGE_TRANSPORTER;
  const changeCustomer = evInvHeaderPermissions?.FORM?.CHANGE_CUSTOMER;

  const [invCustomer, setInvCustomer] = useState(null);
  const [invCustomerName, setInvCustomerName] = useState(null);
  const [transporter, setTransporter] = useState(null);
  const [transporterName, setTransporterName] = useState(null);
  const [resetField, setResetField] = useState(null);

  const isEditMode = useSelector((state) => state.invEv.editMode);
  const categoryId = useSelector((state) => state.invEv.categoryId);
  const withoutTaxTotal = useSelector((state) => state.invEv.withoutTaxTotal);
  const withTaxTotal = useSelector((state) => state.invEv.withTaxTotal);
  const taxAmount = useSelector((state) => state.invEv.taxAmount);
  const charges = useSelector((state) => state.invEv.charges);
  const fromDate = useSelector((state) => state.invEv.fromDate);
  const toDate = useSelector((state) => state.invEv.toDate);

  const [gridData, setGridData] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [status, setStatus] = useState(2);
  const [remark, setRemark] = useState("");
  const stakeholderId = useSelector((state) => state.invEv.stakeholderId);
  const currencyId = useSelector((state) => state.invEv.currencyId);
  const jobId = useSelector((state) => state.invEv.jobId);
  const chhId = useSelector((state) => state.invEv.chhId);
  const statusUpdateRequest = useSelector(
    (state) => state.invEv.statusUpdateRequest,
  );
  const isSubmitLoading = useSelector((state) => state.invEv.submitLoading);

  const [consolidatedRequest, setConsolidatedRequest] = useState(null);
  const jobInfo = useSelector((state) => state.invEv.jobInfo);

  const [isLoading, setIsLoading] = useState(false);
  const [isDataIsLoading, setIsDataLoading] = useState(false);

  const dispatch = useDispatch();

  const getTax = useCallback(
    async (charges, stakeholder) => {
      const taxRequests = charges.map((charge) => ({
        chdId: charge.chdId,
        amount: charge.chdTotal ?? 0.0,
      }));

      const {
        total,
        withTaxTotal: totalWithTax,
        totalTax,
        taxResponses,
      } = await taxCalculate({
        stakeholderId: stakeholder,
        taxRequests,
        isAdHoc: false,
      });

      return {
        total: totalWithTax,
        totalWithoutTax: total,
        totalTax,
        taxResponses: taxResponses.map((tr) => ({
          ...tr,
          jobEvInvoicesTaxDetailRequestList: tr.taxInfoList
            ? tr.taxInfoList.map((ti) => ({
                eitCompany: companyId,
                eitTad: ti.tadId,
                eitTax: ti.taxId,
                taxName: ti.taxTax,
                eitTadValue: ti.taxPercentage,
                eitValue: ti.taxAmount,
                eitStatus: 2,
                eitActive: 1,
                beforeTax: ti.beforeTaxAmount,
                afterTax: ti.afterTaxAmount,
                author: user.username,
              }))
            : [],
        })),
      };
    },
    [stakeholderId],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { status: statusData } = await getBulkDropdownData([
        { key: "status" },
      ]);
      setStatusOptions(statusData);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    setInvCustomer(jobInfo?.invId);
    setInvCustomerName(jobInfo?.invoicingCustomer);
    setTransporter(jobInfo?.transId);
    setTransporterName(jobInfo?.transporterName);
  }, [
    jobInfo?.invId,
    jobInfo?.invoicingCustomer,
    jobInfo?.transId,
    jobInfo?.transporterName,
  ]);

  useEffect(() => {
    if (
      isEditMode ||
      !charges ||
      !jobInfo ||
      charges.length === 0 ||
      (categoryId === 1 && !invCustomer) ||
      (categoryId === 2 && !transporter)
    ) {
      return;
    }
    (async () => {
      setIsDataLoading(true);
      const evInvDetails = [];
      const chargeDetails = [];

      const taxData = await getTax(
        charges,
        categoryId === 1 ? invCustomer : transporter,
      );

      const wtTotal =
        taxData?.total === null || taxData?.total === undefined
          ? 0.0
          : taxData?.total;
      const wotTotal =
        taxData?.totalWithoutTax === null ||
        taxData?.totalWithoutTax === undefined
          ? 0.0
          : taxData?.totalWithoutTax;

      for (let charge of charges) {
        const chargeTaxRes = taxData?.taxResponses?.find(
          (tr) => tr.chdId === charge.chdId,
        );
        const chargeTax = chargeTaxRes ? chargeTaxRes.taxAmount : 0.0;

        evInvDetails.push({
          eidPickChargeId: charge.chdId,
          eidJob: jobId,
          eidChhId: chhId,
          eidCharge: charge.chdChacrges,
          eidDescription: charge.chdDescription?.toString(),
          eidCurrency: currencyId,
          eidTotal: charge.chdTotal,
          eidQty: charge.chdQty,
          eidValue: charge.chdValue,
          eidUom: charge.chdUom,
          eidTax: true,
          eidTaxAmount: chargeTax,
          eidExcId: charge.chdExcId,
          eidExcRate: charge.chdExcRate,
          eidStatus: 2,
          eidActive: 1,
          jobEvInvoicesTaxDetailRequestList:
            chargeTaxRes.jobEvInvoicesTaxDetailRequestList,
        });

        chargeDetails.push({
          ...charge,
          chdTaxAmount: chargeTax,
          chdTotalWithTax: chargeTax + charge.chdTotal,
          jobEvInvoicesTaxDetailRequestList:
            chargeTaxRes.jobEvInvoicesTaxDetailRequestList,
        });
      }
      setConsolidatedRequest({
        jobEvInvoiceHeaderRequest: {
          eihSingleBulk: 1,
          eihCompany: companyId,
          eihType: categoryId,
          eihPickChargeId: chhId,
          eihCustomer: jobInfo?.customerId,
          eihTransporter: jobInfo?.transId,
          eihBillingCustomer: jobInfo?.billId,
          eihInvCustomer: jobInfo?.invId,
          eihJobId: jobId,
          eihJobNo: jobInfo?.jobCode?.toString(),
          eihAddress: "",
          eihBank: null,
          eihAcc: jobInfo?.bilAccNo,
          eihExchange: null,
          eihCurrency: currencyId,
          eihRemarks: "",
          eihWithoutTax: wotTotal,
          eihWithTax: wtTotal,
          eihStatus: 2,
          eihActive: 1,
          type: categoryId,
          eihAdHoc: 2,
        },
        jobEvInvoiceDetailRequests: evInvDetails,
        statusUpdateRequest,
        author: user.username,
      });
      dispatch(
        invEvActions.setTaxInfo({
          withoutTaxTotal: wotTotal,
          withTaxTotal: wtTotal,
          taxAmount: wtTotal - wotTotal,
        }),
      );
      setGridData(chargeDetails);
      setIsDataLoading(false);
    })();
  }, [
    categoryId,
    charges,
    chhId,
    companyId,
    currencyId,
    dispatch,
    getTax,
    invCustomer,
    isEditMode,
    jobId,
    jobInfo,
    jobInfo?.bilAccNo,
    jobInfo?.billId,
    jobInfo?.customerId,
    jobInfo?.invId,
    jobInfo?.jobCode,
    jobInfo?.transId,
    statusUpdateRequest,
    transporter,
    user.username,
  ]);

    const submitHandler = async () => {
        try {
            await dispatch(
                saveSingleInvEv({
                    ...consolidatedRequest,
                    jobEvInvoiceHeaderRequest: {
                        ...consolidatedRequest.jobEvInvoiceHeaderRequest,
                        eihInvCustomer:
                            categoryId === 1
                                ? invCustomer
                                : consolidatedRequest.jobEvInvoiceHeaderRequest?.eihInvCustomer,
                        eihTransporter:
                            categoryId === 2
                                ? transporter
                                : consolidatedRequest.jobEvInvoiceHeaderRequest?.eihTransporter,
                        eihFromDate: fromDate,
                        eihToDate: toDate,
                        eihRemarks: remark,
                        eihStatus: status,
                    },
                })
            ).unwrap();

        } catch (error) {
            console.error("Submission failed:", error);
        }
    };

  return (
    <>
      {isDataIsLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="ui segment">
          <h3 className="ui header">
            {categoryId === 1 ? "Invoice" : "Expense Voucher"} Breakdown
          </h3>

          <div
            className="ui form"
            style={{ marginTop: "10px", marginBottom: "10px" }}
          >
            <>
              {!isEditMode && (
                <>
                  <div className="ui segment">
                    <div className="three fields">
                      <Input
                        field={"eih_without_tax"}
                        module={MODULES.Finance}
                        editMode={isEditMode}
                        form={HEADER_FORM}
                        type="text"
                        label="Without Tax"
                        placeholder="Without Tax"
                        readOnly={true}
                        value={
                          formatter
                            ? formatter.format(withoutTaxTotal)
                            : withTaxTotal
                        }
                      />
                      <Input
                        field={"eih_with_tax"}
                        module={MODULES.Finance}
                        editMode={isEditMode}
                        form={HEADER_FORM}
                        type="text"
                        label="Tax"
                        placeholder="Tax"
                        readOnly={true}
                        value={
                          formatter ? formatter.format(taxAmount) : taxAmount
                        }
                      />
                      <Input
                        field={"eih_total"}
                        module={MODULES.Finance}
                        editMode={isEditMode}
                        form={HEADER_FORM}
                        type="text"
                        label="Total"
                        placeholder="Total"
                        readOnly={true}
                        value={
                          formatter
                            ? formatter.format(withTaxTotal)
                            : withTaxTotal
                        }
                      />
                    </div>
                  </div>
                  <ChargeTaxBreakdown
                    formatter={formatter}
                    selectedCharge={
                      selectedCharge
                        ? {
                            ...selectedCharge,
                            jobEvInvoicesTaxDetailList:
                              selectedCharge.jobEvInvoicesTaxDetailRequestList,
                          }
                        : null
                    }
                  />
                  <Grid
                    cellClickHandler={(e) => {
                      setSelectedCharge(e.data);
                    }}
                    module={MODULES.Finance}
                    content={gridData}
                    config={[
                      {
                        headerName: "Charges",
                        field: "chgCharges",
                        authenticated: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRenderer: ResizableScrollingTextRenderer,
                        cellRendererParams: (params) => ({
                          threshold: 15,
                        }),
                      },
                      ...gridConfig,
                      {
                        headerName: "TAX",
                        field: "chdTaxAmount",
                        authenticated: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRendererSelector: (param) => {
                          if (param.data.chdTaxAmount === -1) {
                            return {
                              component: () => "Not yet calculated",
                            };
                          }
                          return {
                            component: CurrencyRenderer,
                            params: {
                              formatter: formatter,
                            },
                          };
                        },
                      },
                      {
                        headerName: "With Tax Total",
                        field: "chdTotalWithTax",
                        authenticated: true,
                        module: MODULES.Finance,
                        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
                        cellRendererSelector: (param) => {
                          if (param.data.chdTotalWithTax === -1) {
                            return {
                              component: () => "Not yet calculated",
                            };
                          }
                          return {
                            component: CurrencyRenderer,
                            params: {
                              formatter: formatter,
                            },
                          };
                        },
                      },
                    ]}
                  />
                </>
              )}
            </>
            <div className="ui form" style={{ marginTop: "10px" }}>
              <div
                className={
                  (categoryId === 1 && changeCustomer) ||
                  (categoryId === 2 && changeTransporter)
                    ? isEditMode
                      ? "two field"
                      : "three fields"
                    : isEditMode
                      ? "field"
                      : "two fields"
                }
              >
                <Input
                  required={isEditMode}
                  field={"eih_remarks"}
                  module={MODULES.Finance}
                  editMode={isEditMode}
                  form={HEADER_FORM}
                  authenticated={true}
                  type="text"
                  label="Remarks"
                  placeholder="Remarks"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
                {((categoryId === 1 && changeCustomer) ||
                  (categoryId === 2 && changeTransporter)) && (
                  <SearchDropdown
                    key={1}
                    editMode={false}
                    reset={resetField}
                    setReset={setResetField}
                    onResultSelect={(data) => {
                      if (categoryId === 1) {
                        setInvCustomer(data.value);
                        setInvCustomerName(data.title);
                      } else {
                        setTransporter(data.value);
                        setTransporterName(data.title);
                      }
                    }}
                    returnFullObject={true}
                    api={getOtherRefDetails}
                    multiGrid={true}
                    multiGridCurrentValue={
                      categoryId === 1 ? invCustomerName : transporterName
                    }
                    apiDependency={{
                      type: "stakeholder_type",
                      value:
                        categoryId === 1 ? "Invoicee Customer" : "Transporter",
                    }}
                    label={
                      categoryId === 1 ? "Invoicee Customer" : "Transporter"
                    }
                  />
                )}
                {!isEditMode && (
                  <Dropdown
                    field={"eih_status"}
                    module={MODULES.Finance}
                    editMode={isEditMode}
                    form={HEADER_FORM}
                    authenticated={true}
                    required={true}
                    clearable={false}
                    readOnly={isEditMode || isLoading}
                    label="Status"
                    placeholder={isLoading ? "Loading ..." : "Status"}
                    options={statusOptions}
                    style={{ height: "20px" }}
                    value={status}
                    onChange={(e, data) => {
                      setStatus(data.value);
                    }}
                  />
                )}
              </div>
              <div
                className="two fields"
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  flip={true}
                  label="Proceed"
                  Icon={FiChevronsRight}
                  isLoading={isSubmitLoading}
                  create={!isEditMode}
                  onClick={submitHandler}
                  form={HEADER_FORM}
                  module={MODULES.Finance}
                  authenticated={true}
                  editMode={isEditMode}
                  disabled={
                    (categoryId === 1 && !singleGenInvPermission) ||
                    (categoryId === 2 && !singleGenEvPermission)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SingleTaxBreakdown;
